import React, { useId } from 'react';
import { Globe } from 'lucide-react';

import { StatusBadge } from './StatusBadge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';
import { VISIBILITY_COPY, visibilityConsequence, type VisibilityKind } from '../lib/visibilityCopy';

interface VisibilityFieldProps {
  kind: VisibilityKind;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /**
   * 'form' (default): saved with the form, so a Checkbox. 'immediate': takes
   * effect when flipped, so a Switch — which then owes a pending state and the
   * confirmation ladder (DESIGN_SYSTEM.md §5 Boolean controls).
   */
  commit?: 'form' | 'immediate';
  /** While a request runs. Never a permission gate: readers get `readOnly`. */
  disabled?: boolean;
  /** A refusal about visibility itself (a private template still in public lenses). */
  error?: string | null;
  /** The reader's view: the Public/Private badge and the consequence, no control. */
  readOnly?: boolean;
  className?: string;
}

/**
 * Public/private on any record (sensor, lens, template): one bordered row with
 * a Globe, a FIXED label, and the consequence of the current value as help,
 * linked by aria-describedby. The label never flips with state; the control's
 * checked state and the help line carry it. Copy lives in lib/visibilityCopy.ts.
 *
 * Unlike Field, an error does not replace the help: the consequence must stay
 * readable while a refusal shows, so the error is a second line beneath it.
 *
 * Not built on Field orientation="horizontal" (display:contents needs a
 * two-column grid parent, which none of these surfaces has).
 */
export const VisibilityField: React.FC<VisibilityFieldProps> = ({
  kind,
  checked,
  onCheckedChange,
  commit = 'form',
  disabled = false,
  error,
  readOnly = false,
  className,
}) => {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const copy = VISIBILITY_COPY[kind];
  const consequence = visibilityConsequence(kind, checked);

  if (readOnly) {
    return (
      <div className={cn('space-y-1.5 rounded-md border p-3', className)} data-visibility-field="">
        <div className="flex items-center gap-2">
          <Globe aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Visibility</span>
          <StatusBadge status={checked ? 'public' : 'private'} />
        </div>
        <p className="text-xs text-muted-foreground">{consequence}</p>
      </div>
    );
  }

  const describedBy = error ? `${helpId} ${errorId}` : helpId;
  const controlProps = {
    id,
    checked,
    disabled,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
  };

  return (
    <div className={cn('flex items-start gap-3 rounded-md border p-3', className)} data-visibility-field="">
      <div className="flex pt-0.5">
        {commit === 'immediate' ? (
          <Switch {...controlProps} onCheckedChange={(next) => onCheckedChange?.(next)} />
        ) : (
          <Checkbox {...controlProps} onCheckedChange={(next) => onCheckedChange?.(next === true)} />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <Label htmlFor={id} className="flex cursor-pointer items-center gap-1.5 leading-tight">
          <Globe aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          {copy.label}
        </Label>
        <p id={helpId} className="text-xs text-muted-foreground">
          {consequence}
        </p>
        {error ? (
          <p id={errorId} role="alert" className="text-xs text-destructive-emphasis">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};
